// src/utils/freeformQuoteUtils.js
// Freeform quote number generation + PDF rendering.

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { supabase } from '../services/supabase';
import { savePdfWithDialog } from './saveFile';

const PRIMARY = '#384f77';

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Image load failed: ${src}`));
    setTimeout(() => reject(new Error('Image load timeout')), 5000);
    img.src = src;
  });

const sanitizeFilename = (s) => String(s || 'quote').replace(/[^a-z0-9._-]/gi, '_');
const fmtMoney = (n) => `$${Number(n || 0).toFixed(2)}`;
const fmtDate = (d) => (d ? new Date(d + 'T00:00:00').toLocaleDateString() : '—');

/**
 * Suggest the next freeform quote number FQ-YYYY-NNNN. Year-scoped sequence.
 */
export const generateFreeformQuoteNumber = async () => {
  const year = new Date().getFullYear();
  const prefix = `FQ-${year}-`;
  const { data, error } = await supabase
    .from('freeform_quotes')
    .select('quote_number')
    .like('quote_number', `${prefix}%`)
    .order('quote_number', { ascending: false })
    .limit(1);
  if (error) throw error;

  let next = 1;
  if (data && data.length > 0) {
    const match = data[0].quote_number.match(/^FQ-\d{4}-(\d+)$/);
    if (match) next = parseInt(match[1], 10) + 1;
  }
  return `${prefix}${String(next).padStart(4, '0')}`;
};

const customerToAddresses = (customer) => {
  if (!customer) return { billTo: { name: '—' }, shipTo: { name: '—' } };
  const name = customer.nickname || customer.name || '—';
  return {
    billTo: {
      name,
      contact: customer.billing_contact_name,
      address: customer.billing_address,
      city: customer.billing_city,
      state: customer.billing_state,
      zip: customer.billing_zip,
      phone: customer.billing_contact_phone,
      email: customer.billing_contact_email,
    },
    shipTo: {
      name,
      contact: customer.shipping_contact_name,
      address: customer.shipping_address,
      city: customer.shipping_city,
      state: customer.shipping_state,
      zip: customer.shipping_zip,
      phone: customer.shipping_contact_phone,
      email: customer.shipping_contact_email,
    },
  };
};

const drawAddressBlock = (doc, x, y, heading, addr) => {
  doc.setFontSize(10);
  doc.setTextColor(PRIMARY);
  doc.setFont('helvetica', 'bold');
  doc.text(heading, x, y);
  doc.setTextColor(0, 0, 0);
  doc.setFont('helvetica', 'normal');
  let yy = y + 6;
  const lines = [];
  if (addr.name) lines.push(addr.name);
  if (addr.contact) lines.push(addr.contact);
  if (addr.address) lines.push(addr.address);
  const csz = [addr.city, addr.state].filter(Boolean).join(', ');
  if (csz || addr.zip) lines.push(`${csz}${csz && addr.zip ? ' ' : ''}${addr.zip || ''}`);
  if (addr.phone) lines.push(`Phone: ${addr.phone}`);
  if (addr.email) lines.push(`Email: ${addr.email}`);
  if (lines.length === 0) lines.push('—');
  lines.forEach((line) => { doc.text(line, x, yy); yy += 6; });
  return yy;
};

/**
 * Generate and download a freeform quote PDF.
 * @param {object} quote    - freeform_quotes row
 * @param {Array}  lines    - freeform_quote_lines rows
 * @param {object} customer - joined customers row (or null)
 */
export const generateFreeformQuotePDF = async ({ quote, lines, customer }) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();

  // Preload images
  let logoImg = null;
  let miaImg = null;
  try { logoImg = await loadImage(require('../assets/acaLogo.png')); } catch (e) { console.error(e); }
  try { miaImg = await loadImage(require('../assets/madeInAmerica.png')); } catch (e) { console.error(e); }

  // Letterhead
  doc.setFontSize(24);
  doc.setTextColor(PRIMARY);
  doc.text('QUOTE', pageWidth / 2, 14, { align: 'center' });

  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(PRIMARY);
  doc.text(`Quote: ${quote.quote_number || 'N/A'}`, 15, 20);

  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(PRIMARY);
  doc.text('Advanced Controls & Automation', 15, 30);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(0, 0, 0);
  doc.text('574 Hudson River Road', 15, 36);
  doc.text('Waterford, NY 12188', 15, 42);
  doc.text('518-369-4408', 15, 48);
  doc.text('sales@acamachine.com', 15, 54);

  if (logoImg) {
    try { doc.addImage(logoImg, 'PNG', pageWidth - 45, 30, 30, 30); }
    catch (err) { console.error('addImage logo failed:', err); }
  }

  // Bill-to / Ship-to
  const { billTo, shipTo } = customerToAddresses(customer);
  const billBottom = drawAddressBlock(doc, 15, 72, 'Bill To:', billTo);
  const shipBottom = drawAddressBlock(doc, pageWidth / 2 + 5, 72, 'Ship To:', shipTo);

  // Compact details
  const validUntil = () => {
    if (!quote.quote_date) return '—';
    const dt = new Date(quote.quote_date + 'T00:00:00');
    dt.setDate(dt.getDate() + (quote.quote_valid_days || 30));
    return dt.toLocaleDateString();
  };

  autoTable(doc, {
    startY: Math.max(billBottom, shipBottom) + 4,
    body: [
      ['Quote #',       quote.quote_number || '—', 'Quote Date',  fmtDate(quote.quote_date)],
      ['RFQ / PO',      quote.customer_rfq_po || '—', 'Valid Until', validUntil()],
      ['Description',   quote.description || '—', 'Lead Time',   quote.lead_time || '—'],
      ['Payment Terms', quote.payment_terms || '—', 'Shipping',    quote.shipping_fob || '—'],
      ['Quoted By',     quote.quoted_by || '—', '', ''],
    ],
    theme: 'plain',
    styles: { fontSize: 9, cellPadding: 1.5 },
    columnStyles: {
      0: { fontStyle: 'bold', textColor: PRIMARY, cellWidth: 32 },
      1: { cellWidth: 'auto' },
      2: { fontStyle: 'bold', textColor: PRIMARY, cellWidth: 30 },
      3: { cellWidth: 'auto' },
    },
    margin: { left: 15, right: 15 },
  });

  // Line items
  const sorted = [...(lines || [])].sort((a, b) => (a.line_number || 0) - (b.line_number || 0));
  const lineRows = sorted.length > 0
    ? sorted.map((l) => [
        String(l.line_number ?? ''),
        l.description || '—',
        Number(l.quantity || 0).toString(),
        l.uom || 'ea',
        fmtMoney(l.unit_price),
        fmtMoney(Number(l.quantity || 0) * Number(l.unit_price || 0)),
      ])
    : [['—', '(no line items)', '—', '—', '—', '—']];

  autoTable(doc, {
    startY: doc.lastAutoTable.finalY + 4,
    head: [['#', 'DESCRIPTION', 'QTY', 'UOM', 'UNIT PRICE', 'EXTENDED']],
    body: lineRows,
    theme: 'grid',
    styles: { fontSize: 9, cellPadding: 2, textColor: [0, 0, 0] },
    headStyles: { fillColor: PRIMARY, textColor: [255, 255, 255], fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [240, 240, 240] },
    margin: { left: 15, right: 15, bottom: 35 },
    columnStyles: {
      0: { cellWidth: 12, halign: 'right' },
      1: { cellWidth: 'auto' },
      2: { cellWidth: 16, halign: 'right' },
      3: { cellWidth: 16 },
      4: { cellWidth: 28, halign: 'right' },
      5: { cellWidth: 30, halign: 'right' },
    },
    didDrawPage: () => {
      if (miaImg) {
        try {
          const pageHeight = doc.internal.pageSize.getHeight();
          doc.addImage(miaImg, 'PNG', pageWidth - 67.5, pageHeight - 25, 52.5, 15);
        } catch (err) { console.error('addImage MIA failed:', err); }
      }
    },
  });

  // Total
  const subtotal = sorted.reduce(
    (s, l) => s + Number(l.quantity || 0) * Number(l.unit_price || 0),
    0
  );
  doc.setFontSize(12);
  doc.setTextColor(PRIMARY);
  doc.setFont('helvetica', 'bold');
  doc.text(`Subtotal: ${fmtMoney(subtotal)}`, pageWidth - 15, doc.lastAutoTable.finalY + 10, { align: 'right' });

  // Notes + signature
  let y = doc.lastAutoTable.finalY + 20;
  if (quote.customer_notes) {
    doc.setFontSize(10);
    doc.setTextColor(PRIMARY);
    doc.setFont('helvetica', 'bold');
    doc.text('Notes:', 15, y);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
    doc.text(quote.customer_notes, 15, y + 5, { maxWidth: pageWidth - 30 });
    y += 5 + Math.ceil(quote.customer_notes.length / 90) * 5;
  }

  doc.setFontSize(8);
  doc.setTextColor(80, 80, 80);
  doc.text(`Prepared by: ${quote.quoted_by || '—'}`, 15, y + 10);
  doc.text('Customer Approval: ____________________________', pageWidth - 95, y + 10);

  await savePdfWithDialog(doc, `${sanitizeFilename(quote.quote_number)}.pdf`);
};
